import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useEffect, useMemo, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from '../../hooks';
import { ServicesContext } from "../../contexts";
import { getDaysWithAvailableTimeSlots } from "../../utils";
import { AppContext } from "../../contexts";
import useDateTime from "../../hooks/useDateTime";
dayjs.extend(utc);
dayjs.extend(timezone);

function useBook () {
  const [currentWeek, setCurrentWeek] = useState();
  const [currentMonth, setCurrentMonth] = useState();
  const [loadingDatesMonthView, setLoadingDatesMonthView] = useState(0);
  const [selectedDay, setSelectedDay] = useState();
  const [timeSlots, setTimeSlots] = useState([]);
  const [appointmentsLoading, satAppointmentsLoading] = useState(true);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState();
  const [isSubmitting, setIsSubmitting] = useState();
  const { state } = useLocation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { fetchBookingDetails, confirmBooking } = useContext(ServicesContext);
  const { setCurrentWeek : setCurrentWeekNumber, setCurrentMonth: setCurrentMonthNumber, appointmentData} = useContext(AppContext);
  const { isMonthViewEnabled } = appointmentData;
  const { formatTime } = useDateTime();
  const [errorMessage, setErrorMessage] = useState();

  const selectedDayTimeSlots = useMemo(() => {
    if (selectedDay === undefined) return [];
    const appointmentArray = timeSlots.find((el) => new Date(el[0]?.startTimeInUTC).getUTCDate() ==  selectedDay) || [];
    return appointmentArray.map(time => {
       const startTimeCalc = formatTime(time.startTimeInUTC);
       const endTimeCalc = formatTime(time.endTimeInUTC); 
      return {
        label: `${startTimeCalc} - ${endTimeCalc}`,
        value: time
      }
    }
    );
  }, [selectedDay, timeSlots, formatTime]);
  const daysWithAvailableTimeSlots = getDaysWithAvailableTimeSlots(timeSlots);

  const fetchTimeSlots = async () => {
    if (!currentWeek) return;
    satAppointmentsLoading(true);
    const { times, responseWeekNumber : newCurrentWeek } = await fetchBookingDetails(dayjs(currentWeek).format('YYYY-MM-DD'));
    satAppointmentsLoading(false);
    setTimeSlots(times);
    if (newCurrentWeek !== undefined) {
      setCurrentWeekNumber(Number(newCurrentWeek));
    }
  }

  const fetchTimeSlotsForMonthView = async () => {
    if (!currentMonth) return;
    satAppointmentsLoading(true);
    const today = dayjs();
    let currentDate = dayjs(currentMonth).startOf('month');
    const lastDayOfMonth = dayjs(currentMonth).endOf('month');
    // If today is in the same month and year, start from today
    if (today.month() === currentDate.month() && today.year() === currentDate.year()) {
      currentDate = today.clone();
    }
    const requests = [];
    let tempDate = currentDate.clone();
  
    for (let i = 0; i < 6; i++) {
      let weekStart = tempDate.clone();
      let weekEnd = tempDate.clone().endOf('week');
  
      if (weekEnd.isAfter(lastDayOfMonth)) {
        weekEnd = lastDayOfMonth.clone();
      }
  
      // Queue the API request
      requests.push(
        fetchBookingDetails(weekStart.format('YYYY-MM-DD'), weekEnd.format('YYYY-MM-DD'))
      );
  
      // Move to the next week's start
      tempDate = weekEnd.clone().add(1, 'day');
      if (tempDate.isAfter(lastDayOfMonth)) break;
    }
    // Await all requests in parallel
    try {
      const results = await Promise.all(requests);
      const allTimes = results.flatMap(res => res.times);
      setTimeSlots(allTimes);
    } catch (error) {
      console.error('Error fetching booking details:', error);
    }
    satAppointmentsLoading(false);
    setLoadingDatesMonthView(lastDayOfMonth.date() + 1);
  };

  const handleBookingConfirm = async () => {
    setIsSubmitting(true);
    const message = await confirmBooking(selectedTimeSlot, state?.isReschedule);
    if(message.cancelledTask){
      navigate('/message', {
        state: {
          title: i18n('cp_appointment_cancelled'),
          copy:  i18n('cp_task_cancelled'),
          status: false, 
          errorMessage: i18n('cp_task_cancelled'), 
        }
      });
    }else if(message.slotUnavailable){
      setErrorMessage(i18n('cp_slot_not_available'));
      setIsSubmitting(false);
      isMonthViewEnabled ? fetchTimeSlotsForMonthView() : fetchTimeSlots();
    }else{
      navigate('/message', { state: {
        ...message,
        cta: i18n('cp_track_your_appointment'),
        onCtaClick: navigate.bind(null, '/manage')
      }})
    }
    
  }

  const handleRescheduleCancel = () => {
    navigate('/manage');
  }

  useEffect(() => {
    isMonthViewEnabled ? fetchTimeSlotsForMonthView() : fetchTimeSlots();
    setSelectedDay();
  }, [currentWeek, currentMonth]);

  useEffect(() => {
    setSelectedTimeSlot();
  }, [selectedDay]);

  return {
    currentWeek,
    setCurrentWeek,
    currentMonth,
    setCurrentMonth,
    selectedDay,
    setSelectedDay,
    appointmentsLoading,
    daysWithAvailableTimeSlots,
    selectedDayTimeSlots,
    setSelectedTimeSlot,
    handleBookingConfirm,
    isSubmitting,
    isValid: selectedTimeSlot && typeof selectedDay === 'number',
    handleRescheduleCancel,
    errorMessage,
    setErrorMessage,
    loadingDatesMonthView,
    setLoadingDatesMonthView,
    setTimeSlots,
    selectedTimeSlot
  }
}

export default useBook;
